import axios from "vue-app/utils/axios";
import { DTOProductPreviewResponse, DTOTrackerProduct } from "vue-app/api/dto";
import { BasicProductData } from "vue-app/utils/functions";

const seededShuffle = <T>(array: T[], seed: number): T[] => {
  const shuffled = [...array];
  let currentSeed = seed;

  const random = () => {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    return currentSeed / 233280;
  };

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

let pendingRequest: Promise<BasicProductData[]> | null = null;

export const getExampleProducts = async (seed?: number) => {
  const cacheKey = `${window.EDRONE_CONTEXT.trackerId}__example-products-v2`;
  const cachedProducts = localStorage.getItem(cacheKey);

  if (cachedProducts) {
    try {
      const parsed: BasicProductData[] = JSON.parse(cachedProducts);
      if (Array.isArray(parsed) && parsed.length >= 3) {
        return seed !== undefined ? seededShuffle(parsed, seed) : parsed;
      }
    } catch {
      localStorage.removeItem(cacheKey);
    }
  }

  if (!pendingRequest) {
    pendingRequest = (async () => {
      try {
        const { data: products } = await axios.get<DTOTrackerProduct[]>("/products/example");

        const finalData = products?.length
          ? products
          : (
              await axios.get<DTOTrackerProduct[]>("/products/scrapped", {
                params: { url: window.EDRONE_CONTEXT.user.companyUrl },
              })
            ).data;

        const basicDataMap = finalData.map((product) => {
          return {
            title: product.productTitle,
            price: product.actualPrice,
            oldPrice: product.productPrice,
            image: product.image,
          } as BasicProductData;
        });

        while (basicDataMap.length < 3 && basicDataMap.length > 0) {
          const productToDuplicate = basicDataMap[basicDataMap.length % basicDataMap.length];
          basicDataMap.push({ ...productToDuplicate });
        }

        if (basicDataMap.length >= 3) {
          localStorage.setItem(cacheKey, JSON.stringify(basicDataMap));
        }
        return basicDataMap;
      } catch (e) {
        console.error(e);
        return [];
      } finally {
        pendingRequest = null;
      }
    })();
  }

  const data = await pendingRequest;
  return seed !== undefined ? seededShuffle(data, seed) : data;
};

export const getProductsPreview = async () => {
  const { data } = await axios.get<DTOProductPreviewResponse>("/products/products-preview");
  return data;
};
